import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { PlanReviewBoundingBox } from "@/api/plan_review";

const EMPTY_PAGE_IMAGES: Record<string, PlanReviewPageImage> = {};
const PDF_JS_VERSION = "3.11.174";
const PDF_JS_SCRIPT_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDF_JS_VERSION}/pdf.min.js`;
const PDF_JS_WORKER_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDF_JS_VERSION}/pdf.worker.min.js`;
const DEFAULT_VIEWER_WIDTH = 900;

type PdfViewport = {
  width: number;
  height: number;
};

type PdfRenderTask = {
  promise: Promise<void>;
  cancel: () => void;
};

type PdfPage = {
  getViewport: (options: { scale: number; rotation?: number }) => PdfViewport;
  render: (options: { canvasContext: CanvasRenderingContext2D; viewport: PdfViewport }) => PdfRenderTask;
};

type PdfDocument = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfPage>;
};

type PdfJsLib = {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument: (options: { url: string; withCredentials?: boolean }) => { promise: Promise<PdfDocument> };
};

declare global {
  interface Window {
    pdfjsLib?: PdfJsLib;
    __planReviewPdfJsPromise?: Promise<PdfJsLib>;
  }
}

export type PlanReviewPin = {
  id: number;
  number: number;
  page: number;
  bbox: number[];
};

export type PlanReviewPageCrop = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type PlanReviewPageImage = {
  url: string;
  width: number;
  height: number;
  crop?: PlanReviewPageCrop | null;
};

type PlanReviewPdfViewerProps = {
  boxes: PlanReviewBoundingBox[];
  annotatedPlanUrl?: string | null;
  sourcePlanUrl?: string | null;
  focusedKey?: string | null;
  focusedLabel?: string | null;
  page: number | null;
  pdfUrls: string[];
  pageImages?: Record<string, PlanReviewPageImage>;
  onPageChange?: (page: number) => void;
  pins?: PlanReviewPin[];
  pinMode?: boolean;
  onPinPlaced?: (page: number, bbox: number[]) => void;
  focusedPinId?: number | null;
  onPinClick?: (pinId: number) => void;
};

type NormalizedPoint = { x: number; y: number };

/** Inverse of the overlay render math: pointer position -> normalized 0..1 page coords. */
export function pointerToNormalized(rect: DOMRect, clientX: number, clientY: number): NormalizedPoint {
  const x = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
  const y = Math.min(Math.max((clientY - rect.top) / rect.height, 0), 1);
  return { x, y };
}

/** Click (below the drag threshold) -> zero-area point pin; drag -> ordered region bbox. */
export function buildAnchorBbox(start: NormalizedPoint, end: NormalizedPoint, dragThreshold = 0.004): number[] {
  if (Math.abs(end.x - start.x) < dragThreshold && Math.abs(end.y - start.y) < dragThreshold) {
    return [start.x, start.y, start.x, start.y];
  }
  return [
    Math.min(start.x, end.x),
    Math.min(start.y, end.y),
    Math.max(start.x, end.x),
    Math.max(start.y, end.y),
  ];
}

function clampUnit(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(Math.max(value, 0), 1);
}

/** Keep overlay boxes on the rendered page even when upstream bbox coords drift outside 0..1. */
export function clampNormalizedBbox(bbox: number[]): [number, number, number, number] {
  const x1 = clampUnit(bbox[0]);
  const y1 = clampUnit(bbox[1]);
  const x2 = clampUnit(bbox[2]);
  const y2 = clampUnit(bbox[3]);
  return [Math.min(x1, x2), Math.min(y1, y2), Math.max(x1, x2), Math.max(y1, y2)];
}

function cropFrame(page: PlanReviewPageImage) {
  const crop = page.crop;
  if (crop && page.width > 0 && page.height > 0 && crop.width > 0 && crop.height > 0) {
    return {
      x: crop.x,
      y: crop.y,
      width: crop.width,
      height: crop.height,
      pageWidth: page.width,
      pageHeight: page.height,
    };
  }
  return null;
}

export function pageImageDisplaySize(page: PlanReviewPageImage) {
  const frame = cropFrame(page);
  if (frame) return { width: frame.width, height: frame.height };
  return { width: page.width, height: page.height };
}

/** Raster for this page only. A gap keeps PDF.js active instead of an empty image pane. */
export function pageImageForViewer(
  pageImages: Record<string, PlanReviewPageImage>,
  page: number
): PlanReviewPageImage | undefined {
  const image = pageImages[String(page)];
  return image?.url ? image : undefined;
}

/** Prefer the PDF page count. Raster keys are only a stand-in until that metadata loads. */
export function visibleViewerPageCount(pdfPageCount: number, rasterPageCount: number) {
  return pdfPageCount > 0 ? pdfPageCount : rasterPageCount;
}

/** Map a 0-1 uncropped page bbox onto the cropped raster the viewer shows. */
export function pageBboxToImageBbox(bbox: number[], page: PlanReviewPageImage): number[] {
  const frame = cropFrame(page);
  if (!frame) return bbox;
  return [
    (bbox[0] * frame.pageWidth - frame.x) / frame.width,
    (bbox[1] * frame.pageHeight - frame.y) / frame.height,
    (bbox[2] * frame.pageWidth - frame.x) / frame.width,
    (bbox[3] * frame.pageHeight - frame.y) / frame.height,
  ];
}

/** Map a bbox on the cropped raster back onto the uncropped page. */
export function imageBboxToPageBbox(bbox: number[], page: PlanReviewPageImage): number[] {
  const frame = cropFrame(page);
  if (!frame) return bbox;
  return [
    (bbox[0] * frame.width + frame.x) / frame.pageWidth,
    (bbox[1] * frame.height + frame.y) / frame.pageHeight,
    (bbox[2] * frame.width + frame.x) / frame.pageWidth,
    (bbox[3] * frame.height + frame.y) / frame.pageHeight,
  ];
}

/** Normalized coordinates covering the entire rendered page. */
export const FULL_PAGE_BBOX: [number, number, number, number] = [0, 0, 1, 1];

export function isFullPageBbox(bbox: number[], coverageThreshold = 0.98, edgeSlop = 0.02) {
  const [x1, y1, x2, y2] = clampNormalizedBbox(bbox);
  const width = x2 - x1;
  const height = y2 - y1;
  return (
    x1 <= edgeSlop &&
    y1 <= edgeSlop &&
    x2 >= 1 - edgeSlop &&
    y2 >= 1 - edgeSlop &&
    width >= coverageThreshold &&
    height >= coverageThreshold
  );
}

/** Snap near-full-page boxes to a true full-page overlay so they fill the viewer. */
export function normalizeHighlightBbox(bbox: number[]): [number, number, number, number] {
  const clamped = clampNormalizedBbox(bbox);
  return isFullPageBbox(clamped) ? FULL_PAGE_BBOX : clamped;
}

export function bboxToOverlayStyle(bbox: number[], options?: { minSizePercent?: number }) {
  if (isFullPageBbox(bbox)) {
    return {
      left: "0%",
      top: "0%",
      width: "100%",
      height: "100%",
    };
  }

  const [x1, y1, x2, y2] = normalizeHighlightBbox(bbox);
  const minSize = options?.minSizePercent ?? 0.15;
  return {
    left: `${x1 * 100}%`,
    top: `${y1 * 100}%`,
    width: `${Math.max((x2 - x1) * 100, minSize)}%`,
    height: `${Math.max((y2 - y1) * 100, minSize)}%`,
  };
}

export function isTinyBbox(bbox: number[], thresholdPercent = 0.8) {
  if (isFullPageBbox(bbox)) return false;
  const [x1, y1, x2, y2] = clampNormalizedBbox(bbox);
  return (x2 - x1) * 100 < thresholdPercent && (y2 - y1) * 100 < thresholdPercent;
}

function overlayBoxClass(isFocused: boolean, fullPage: boolean, stage?: "finder" | "extracted") {
  if (isFocused) {
    return fullPage ? "border-blue-500/40 bg-blue-500/8" : "border-blue-600/50 bg-blue-500/15";
  }

  switch (stage) {
    case "extracted":
      return fullPage ? "border-sky-400/35 bg-sky-500/8" : "border-sky-500/40 bg-sky-500/15";
    case "finder":
    case undefined:
      return fullPage ? "border-rose-400/30 bg-rose-500/5" : "border-rose-500/40 bg-rose-500/10";
    default: {
      const _exhaustive: never = stage;
      return _exhaustive;
    }
  }
}

export function bboxCenterMarkerStyle(bbox: number[], markerSizePercent = 2.5) {
  const [x1, y1, x2, y2] = clampNormalizedBbox(bbox);
  const centerX = ((x1 + x2) / 2) * 100;
  const centerY = ((y1 + y2) / 2) * 100;
  return {
    left: `${centerX - markerSizePercent / 2}%`,
    top: `${centerY - markerSizePercent / 2}%`,
    width: `${markerSizePercent}%`,
    height: `${markerSizePercent}%`,
  };
}

function isPointPin(bbox: number[]) {
  return Math.abs(bbox[2] - bbox[0]) < 0.0001 && Math.abs(bbox[3] - bbox[1]) < 0.0001;
}

function clampPage(page: number, pageCount: number) {
  if (pageCount <= 0) return Math.max(page, 1);
  return Math.min(Math.max(page, 1), pageCount);
}

export type PlanReviewViewTransform = {
  zoom: number;
  panX: number;
  panY: number;
};

export const MIN_VIEW_ZOOM = 1;
export const MAX_VIEW_ZOOM = 8;
export const FIT_VIEW: PlanReviewViewTransform = { zoom: 1, panX: 0, panY: 0 };
const BBOX_ZOOM_PADDING = 2.4;
const CITATION_ZOOM_CAP = 2;
const VIEW_ZOOM_STEP = 1.25;
const PAN_DRAG_THRESHOLD_PX = 4;
const MAX_PLAN_CANVAS_WIDTH = 4096;
const ZOOM_RENDER_DEBOUNCE_MS = 160;

export function clampViewZoom(zoom: number) {
  if (!Number.isFinite(zoom)) return MIN_VIEW_ZOOM;
  return Math.min(MAX_VIEW_ZOOM, Math.max(MIN_VIEW_ZOOM, zoom));
}

export function planViewerAcceptsWheelZoom(
  event: Pick<WheelEvent, "ctrlKey" | "metaKey">,
  zoom: number
) {
  return event.ctrlKey || event.metaKey || zoom > MIN_VIEW_ZOOM;
}

/** PDF.js bitmap scale so CSS zoom does not stretch a fit-width canvas. */
export function planPageRenderScale(options: {
  pageWidth: number;
  pageHeight: number;
  containerWidth: number;
  zoom: number;
  devicePixelRatio: number;
}) {
  const fitScale = Math.max(options.containerWidth / options.pageWidth, 0.2);
  const cssWidth = options.pageWidth * fitScale;
  const cssHeight = options.pageHeight * fitScale;
  const dpr = Math.min(Math.max(options.devicePixelRatio || 1, 1), 2);
  const zoom = clampViewZoom(options.zoom);
  const maxZoom = MAX_PLAN_CANVAS_WIDTH / Math.max(cssWidth * dpr, 1);
  const qualityZoom = Math.min(zoom, Math.max(maxZoom, 1));
  return {
    fitScale,
    pdfScale: fitScale * dpr * qualityZoom,
    cssWidth,
    cssHeight,
  };
}

export function zoomAroundPoint(
  current: PlanReviewViewTransform,
  nextZoom: number,
  pointX: number,
  pointY: number
): PlanReviewViewTransform {
  const zoom = clampViewZoom(nextZoom);
  if (zoom === current.zoom) return { zoom, panX: current.panX, panY: current.panY };
  const pageX = (pointX - current.panX) / current.zoom;
  const pageY = (pointY - current.panY) / current.zoom;
  return {
    zoom,
    panX: pointX - pageX * zoom,
    panY: pointY - pageY * zoom,
  };
}

export function panView(current: PlanReviewViewTransform, dx: number, dy: number): PlanReviewViewTransform {
  return { ...current, panX: current.panX + dx, panY: current.panY + dy };
}

export function clampViewPan(
  view: PlanReviewViewTransform,
  pageWidth: number,
  pageHeight: number,
  viewportWidth: number,
  viewportHeight: number
): PlanReviewViewTransform {
  const zoom = clampViewZoom(view.zoom);
  const scaledW = pageWidth * zoom;
  const scaledH = pageHeight * zoom;
  let panX = view.panX;
  let panY = view.panY;

  if (scaledW <= viewportWidth) {
    panX = (viewportWidth - scaledW) / 2;
  } else {
    panX = Math.min(0, Math.max(viewportWidth - scaledW, panX));
  }

  if (scaledH <= viewportHeight) {
    panY = (viewportHeight - scaledH) / 2;
  } else {
    panY = Math.min(0, Math.max(viewportHeight - scaledH, panY));
  }

  return { zoom, panX, panY };
}

export function unionNormalizedBbox(boxes: number[][]): number[] | null {
  if (boxes.length === 0) return null;
  let x1 = Number.POSITIVE_INFINITY;
  let y1 = Number.POSITIVE_INFINITY;
  let x2 = Number.NEGATIVE_INFINITY;
  let y2 = Number.NEGATIVE_INFINITY;
  for (const bbox of boxes) {
    const [a, b, c, d] = clampNormalizedBbox(bbox);
    x1 = Math.min(x1, a);
    y1 = Math.min(y1, b);
    x2 = Math.max(x2, c);
    y2 = Math.max(y2, d);
  }
  return [x1, y1, x2, y2];
}

/** Zoom so the box sits closer in the viewport, centered, without leaving the page. */
export function viewForBbox(
  bbox: number[],
  viewportWidth: number,
  viewportHeight: number,
  pageWidth: number,
  pageHeight: number
): PlanReviewViewTransform {
  if (viewportWidth <= 0 || viewportHeight <= 0 || pageWidth <= 0 || pageHeight <= 0) {
    return FIT_VIEW;
  }
  if (isFullPageBbox(bbox)) return FIT_VIEW;

  const [x1, y1, x2, y2] = clampNormalizedBbox(bbox);
  const boxW = Math.max((x2 - x1) * pageWidth, pageWidth * 0.04);
  const boxH = Math.max((y2 - y1) * pageHeight, pageHeight * 0.04);
  const fitted = Math.min(viewportWidth / (boxW * BBOX_ZOOM_PADDING), viewportHeight / (boxH * BBOX_ZOOM_PADDING));
  const zoom = clampViewZoom(Math.min(fitted, CITATION_ZOOM_CAP));
  const cx = ((x1 + x2) / 2) * pageWidth;
  const cy = ((y1 + y2) / 2) * pageHeight;
  return clampViewPan(
    { zoom, panX: viewportWidth / 2 - cx * zoom, panY: viewportHeight / 2 - cy * zoom },
    pageWidth,
    pageHeight,
    viewportWidth,
    viewportHeight
  );
}

function boxMatchesFocus(box: PlanReviewBoundingBox, focusedKey?: string | null, focusedLabel?: string | null) {
  if (focusedKey) {
    if (box.focusKey === focusedKey) return true;
    if (!focusedKey.includes(":ev:") && !focusedKey.includes(":vi:")) {
      if (box.focusKey?.startsWith(`${focusedKey}:`)) return true;
    }
    return false;
  }
  if (focusedLabel && box.label !== focusedLabel) return false;
  return true;
}

function isBoxFocused(box: PlanReviewBoundingBox, focusedKey?: string | null, focusedLabel?: string | null) {
  if (focusedKey) {
    if (box.focusKey === focusedKey) return true;
    if (!focusedKey.includes(":ev:") && !focusedKey.includes(":vi:") && box.focusKey?.startsWith(`${focusedKey}:`)) {
      return true;
    }
    return false;
  }
  return !!(focusedLabel && box.label === focusedLabel);
}

export function validBoxesForPage(
  boxes: PlanReviewBoundingBox[],
  page: number,
  focusedLabel?: string | null,
  focusedKey?: string | null
) {
  return boxes.filter((box) => {
    if (Number(box.page) !== page) return false;
    if (!Array.isArray(box.bbox) || box.bbox.length !== 4) return false;
    if (!box.bbox.every((value) => typeof value === "number" && Number.isFinite(value))) return false;
    if (box.stream) return true;
    if (!focusedKey && !focusedLabel) return false;
    return boxMatchesFocus(box, focusedKey, focusedLabel);
  });
}

async function loadPdfJsLibrary() {
  if (window.pdfjsLib) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER_URL;
    return window.pdfjsLib;
  }

  if (!window.__planReviewPdfJsPromise) {
    window.__planReviewPdfJsPromise = new Promise<PdfJsLib>((resolve, reject) => {
      const existingScript = document.querySelector('script[data-plan-review-pdfjs="true"]') as HTMLScriptElement | null;
      const handleLoad = () => {
        if (!window.pdfjsLib) {
          reject(new Error("PDF viewer runtime loaded without exposing pdfjsLib."));
          return;
        }

        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER_URL;
        resolve(window.pdfjsLib);
      };
      const handleError = () => reject(new Error("Failed to load PDF viewer runtime."));

      if (existingScript) {
        existingScript.addEventListener("load", handleLoad, { once: true });
        existingScript.addEventListener("error", handleError, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = PDF_JS_SCRIPT_URL;
      script.async = true;
      script.dataset.planReviewPdfjs = "true";
      script.addEventListener("load", handleLoad, { once: true });
      script.addEventListener("error", handleError, { once: true });
      document.body.appendChild(script);
    });
  }

  return window.__planReviewPdfJsPromise;
}

export function PlanReviewPdfViewer({
  boxes,
  annotatedPlanUrl,
  sourcePlanUrl,
  focusedKey,
  focusedLabel,
  page,
  pdfUrls,
  pageImages = EMPTY_PAGE_IMAGES,
  onPageChange,
  pins = [],
  pinMode = false,
  onPinPlaced,
  focusedPinId,
  onPinClick,
}: PlanReviewPdfViewerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const documentCacheRef = useRef<{ pdfUrl: string; promise: Promise<PdfDocument> } | null>(null);
  /** PDF.js allows only one active render per canvas; cancel overlaps from resize/page/url changes. */
  const activeRenderTaskRef = useRef<PdfRenderTask | null>(null);
  const pageSourceKeyRef = useRef("");
  const [containerWidth, setContainerWidth] = useState(DEFAULT_VIEWER_WIDTH);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [renderedSize, setRenderedSize] = useState({ width: 0, height: 0 });
  const [showPins, setShowPins] = useState(true);
  const [dragStart, setDragStart] = useState<NormalizedPoint | null>(null);
  const [dragCurrent, setDragCurrent] = useState<NormalizedPoint | null>(null);
  const [view, setView] = useState<PlanReviewViewTransform>(FIT_VIEW);
  const panDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    panX: number;
    panY: number;
    moved: boolean;
  } | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [renderZoom, setRenderZoom] = useState(1);

  const imagePageCount = useMemo(() => {
    const numbers = Object.keys(pageImages)
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value > 0);
    return numbers.length > 0 ? Math.max(...numbers) : 0;
  }, [pageImages]);
  const visiblePageCount = visibleViewerPageCount(pageCount, imagePageCount);
  const effectivePage = clampPage(page || 1, visiblePageCount);
  const currentPageImage = pageImageForViewer(pageImages, effectivePage);
  const usingPageImages = Boolean(currentPageImage);
  const pageBoxes = useMemo(
    () => validBoxesForPage(boxes, effectivePage, focusedLabel, focusedKey),
    [boxes, effectivePage, focusedKey, focusedLabel]
  );
  const displayBoxes = useMemo(() => {
    if (!currentPageImage) return pageBoxes;
    return pageBoxes.map((box) => ({
      ...box,
      bbox: pageBboxToImageBbox(box.bbox as number[], currentPageImage),
    }));
  }, [currentPageImage, pageBoxes]);
  const pagePins = useMemo(
    () =>
      pins.filter(
        (pin) =>
          pin.page === effectivePage &&
          Array.isArray(pin.bbox) &&
          pin.bbox.length === 4 &&
          pin.bbox.every((value) => typeof value === "number" && Number.isFinite(value))
      ),
    [pins, effectivePage]
  );
  const displayPins = useMemo(() => {
    if (!currentPageImage) return pagePins;
    return pagePins.map((pin) => ({
      ...pin,
      bbox: pageBboxToImageBbox(pin.bbox, currentPageImage),
    }));
  }, [currentPageImage, pagePins]);
  const focusedPageBoxes = useMemo(
    () => displayBoxes.filter((box) => isBoxFocused(box, focusedKey, focusedLabel)),
    [displayBoxes, focusedKey, focusedLabel]
  );
  const focusViewToken = useMemo(() => {
    if (focusedPageBoxes.length === 0) return `fit:${effectivePage}`;
    return `${focusedKey ?? ""}|${focusedLabel ?? ""}|${effectivePage}|${focusedPageBoxes
      .map((box) => (box.bbox ?? []).join(","))
      .join(";")}`;
  }, [effectivePage, focusedKey, focusedLabel, focusedPageBoxes]);

  function applyView(next: PlanReviewViewTransform) {
    const viewport = containerRef.current;
    if (!viewport || renderedSize.width <= 0 || renderedSize.height <= 0) {
      setView(next);
      return;
    }
    setView(clampViewPan(next, renderedSize.width, renderedSize.height, viewport.clientWidth, viewport.clientHeight));
  }

  function zoomBy(factor: number, pointX?: number, pointY?: number) {
    const viewport = containerRef.current;
    const originX = pointX ?? (viewport ? viewport.clientWidth / 2 : 0);
    const originY = pointY ?? (viewport ? viewport.clientHeight / 2 : 0);
    applyView(zoomAroundPoint(view, view.zoom * factor, originX, originY));
  }

  useEffect(() => {
    if (!pinMode) {
      setDragStart(null);
      setDragCurrent(null);
    }
  }, [pinMode]);

  const focusedPageBoxesRef = useRef(focusedPageBoxes);
  useLayoutEffect(() => {
    focusedPageBoxesRef.current = focusedPageBoxes;
  }, [focusedPageBoxes]);

  useEffect(() => {
    const viewport = containerRef.current;
    if (!viewport || renderedSize.width <= 0 || renderedSize.height <= 0) return;

    const focusedBbox = unionNormalizedBbox(focusedPageBoxesRef.current.map((box) => box.bbox as number[]));
    if (!focusedBbox) {
      setView((current) => (current.zoom === 1 && current.panX === 0 && current.panY === 0 ? current : FIT_VIEW));
      return;
    }

    setView(
      viewForBbox(focusedBbox, viewport.clientWidth, viewport.clientHeight, renderedSize.width, renderedSize.height)
    );
  }, [focusViewToken, renderedSize.height, renderedSize.width]);

  useEffect(() => {
    const viewport = containerRef.current;
    if (!viewport) return;

    const onWheel = (event: WheelEvent) => {
      if (!planViewerAcceptsWheelZoom(event, view.zoom)) return;
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      const factor = event.deltaY < 0 ? VIEW_ZOOM_STEP : 1 / VIEW_ZOOM_STEP;
      setView((current) => {
        const next = zoomAroundPoint(
          current,
          current.zoom * factor,
          event.clientX - rect.left,
          event.clientY - rect.top
        );
        if (renderedSize.width <= 0 || renderedSize.height <= 0) return next;
        return clampViewPan(next, renderedSize.width, renderedSize.height, viewport.clientWidth, viewport.clientHeight);
      });
    };

    viewport.addEventListener("wheel", onWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", onWheel);
  }, [renderedSize.height, renderedSize.width, view.zoom]);

  useEffect(() => {
    if (Math.abs(renderZoom - view.zoom) < 0.01) return;
    const timer = window.setTimeout(() => setRenderZoom(view.zoom), ZOOM_RENDER_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [renderZoom, view.zoom]);
  const candidatePdfUrls = useMemo(
    () => pdfUrls.filter((url, index) => url && pdfUrls.indexOf(url) === index),
    [pdfUrls]
  );

  useEffect(() => {
    const syncContainerWidth = () => {
      const width = containerRef.current?.clientWidth || DEFAULT_VIEWER_WIDTH;
      setContainerWidth(width);
    };

    syncContainerWidth();
    if (typeof ResizeObserver === "undefined" || !containerRef.current) return;

    const observer = new ResizeObserver(() => syncContainerWidth());
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;

    function cancelActiveRender() {
      const task = activeRenderTaskRef.current;
      if (!task) return;
      try {
        if (typeof task.cancel === "function") {
          task.cancel();
        }
      } catch {
        // ignore
      }
      activeRenderTaskRef.current = null;
    }

    async function resolvePdfDocument() {
      const pdfjsLib = await loadPdfJsLibrary();
      if (cancelled) return null;

      let pdfDocument: PdfDocument | null = null;
      let lastError: unknown = null;

      const cachedEntry = documentCacheRef.current;
      if (cachedEntry && candidatePdfUrls.includes(cachedEntry.pdfUrl)) {
        try {
          pdfDocument = await cachedEntry.promise;
        } catch (error) {
          lastError = error;
          documentCacheRef.current = null;
        }
      }

      if (!pdfDocument) {
        for (const candidateUrl of candidatePdfUrls) {
          try {
            const documentPromise = pdfjsLib.getDocument({ url: candidateUrl, withCredentials: false }).promise;
            documentCacheRef.current = { pdfUrl: candidateUrl, promise: documentPromise };
            pdfDocument = await documentPromise;
            break;
          } catch (error) {
            lastError = error;
            documentCacheRef.current = null;
          }
        }
      }

      if (!pdfDocument) throw lastError || new Error("Failed to render the plan viewer.");
      return pdfDocument;
    }

    async function renderPage() {
      if (candidatePdfUrls.length === 0) return;

      if (usingPageImages) {
        try {
          const pdfDocument = await resolvePdfDocument();
          if (cancelled || !pdfDocument) return;
          setPageCount(pdfDocument.numPages);
        } catch {
          // Raster navigation still works until a later PDF load succeeds.
        }
        return;
      }

      if (!canvasRef.current) return;

      cancelActiveRender();

      const pageSourceKey = `${candidatePdfUrls.join("|")}|${containerWidth}|${page || 1}`;
      const showLoadingOverlay = pageSourceKeyRef.current !== pageSourceKey;
      if (showLoadingOverlay) {
        setLoading(true);
      }
      setLoadingError(null);

      try {
        const pdfDocument = await resolvePdfDocument();
        if (cancelled || !pdfDocument) return;

        setPageCount(pdfDocument.numPages);
        const nextPage = clampPage(page || 1, pdfDocument.numPages);
        const pdfPage = await pdfDocument.getPage(nextPage);
        if (cancelled) return;

        const baseViewport = pdfPage.getViewport({ scale: 1 });
        const { pdfScale, cssWidth, cssHeight } = planPageRenderScale({
          pageWidth: baseViewport.width,
          pageHeight: baseViewport.height,
          containerWidth,
          zoom: renderZoom,
          devicePixelRatio: window.devicePixelRatio || 1,
        });
        const viewport = pdfPage.getViewport({ scale: pdfScale });
        const canvas = canvasRef.current;
        const context = canvas?.getContext("2d", { alpha: false });

        if (!canvas || !context) {
          throw new Error("Unable to initialize PDF canvas.");
        }

        cancelActiveRender();

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${cssWidth}px`;
        canvas.style.height = `${cssHeight}px`;

        context.setTransform(1, 0, 0, 1, 0, 0);

        if (cancelled) return;

        const renderTask = pdfPage.render({ canvasContext: context, viewport });
        activeRenderTaskRef.current = renderTask;

        try {
          await renderTask.promise;
        } catch (renderErr: unknown) {
          const name = renderErr && typeof renderErr === "object" && "name" in renderErr ? String((renderErr as { name: string }).name) : "";
          const message = renderErr instanceof Error ? renderErr.message : String(renderErr);
          if (cancelled || name === "RenderingCancelledException" || /cancel/i.test(message)) {
            return;
          }
          throw renderErr;
        } finally {
          if (activeRenderTaskRef.current === renderTask) {
            activeRenderTaskRef.current = null;
          }
        }

        if (cancelled) return;

        pageSourceKeyRef.current = pageSourceKey;
        setRenderedSize((current) =>
          current.width === cssWidth && current.height === cssHeight ? current : { width: cssWidth, height: cssHeight }
        );
      } catch (error) {
        if (cancelled) return;
        setLoadingError(error instanceof Error ? error.message : "Failed to render the plan viewer.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void renderPage();

    return () => {
      cancelled = true;
      cancelActiveRender();
    };
  }, [candidatePdfUrls, containerWidth, page, renderZoom, usingPageImages]);

  useEffect(() => {
    if (!currentPageImage?.url) return;

    const { width, height } = pageImageDisplaySize(currentPageImage);
    const cssWidth = containerWidth;
    const cssHeight = cssWidth * (height / Math.max(width, 1));
    setRenderedSize((current) =>
      current.width === cssWidth && current.height === cssHeight ? current : { width: cssWidth, height: cssHeight }
    );
  }, [containerWidth, currentPageImage]);

  useEffect(() => {
    if (!currentPageImage?.url) return;
    setLoading(true);
    setLoadingError(null);
  }, [currentPageImage?.url]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-slate-600">
            Page {effectivePage}
          {visiblePageCount > 0 ? ` of ${visiblePageCount}` : ""}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label="Zoom out"
              onClick={() => zoomBy(1 / VIEW_ZOOM_STEP)}
              disabled={view.zoom <= MIN_VIEW_ZOOM}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="min-w-12 text-center text-xs tabular-nums text-slate-600">{Math.round(view.zoom * 100)}%</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label="Zoom in"
              onClick={() => zoomBy(VIEW_ZOOM_STEP)}
              disabled={view.zoom >= MAX_VIEW_ZOOM}
            >
              <Plus className="h-4 w-4" />
            </Button>
            {(view.zoom !== 1 || view.panX !== 0 || view.panY !== 0) && (
              <Button type="button" variant="ghost" size="sm" onClick={() => applyView(FIT_VIEW)}>
                Reset view
              </Button>
            )}
          </div>
          {visiblePageCount > 1 ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onPageChange?.(Math.max(effectivePage - 1, 1))}
                disabled={effectivePage <= 1}
              >
                Previous page
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onPageChange?.(effectivePage + 1)}
                disabled={effectivePage >= visiblePageCount}
              >
                Next page
              </Button>
            </>
          ) : null}
          {pins.length > 0 && (
            <Button type="button" variant="outline" size="sm" onClick={() => setShowPins((current) => !current)}>
              {showPins ? "Hide pins" : `Show pins (${pins.length})`}
            </Button>
          )}
          {annotatedPlanUrl ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => window.open(annotatedPlanUrl, "_blank", "noopener,noreferrer")}
            >
              Open annotated PDF
            </Button>
          ) : null}
          {sourcePlanUrl ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.open(sourcePlanUrl, "_blank", "noopener,noreferrer")}
            >
              Open original plan
            </Button>
          ) : null}
        </div>
      </div>

      {loadingError && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{loadingError}</div>}

      <div className="rounded border border-slate-200 bg-slate-100 p-2">
        <div
          ref={containerRef}
          data-testid="plan-review-pdf-viewport"
          data-zoom={String(view.zoom)}
          data-pan-x={String(view.panX)}
          data-pan-y={String(view.panY)}
          className={`relative touch-none overflow-hidden rounded select-none ${
            pinMode ? "" : isPanning ? "cursor-grabbing" : "cursor-grab"
          }`}
          style={{
            height: renderedSize.height > 0 ? `${renderedSize.height}px` : "320px",
          }}
          onPointerDown={(event) => {
            if (pinMode || event.button !== 0) return;
            if (event.target instanceof HTMLElement && event.target.closest("button")) return;
            panDragRef.current = {
              pointerId: event.pointerId,
              startX: event.clientX,
              startY: event.clientY,
              panX: view.panX,
              panY: view.panY,
              moved: false,
            };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            const drag = panDragRef.current;
            if (!drag || drag.pointerId !== event.pointerId) return;
            const dx = event.clientX - drag.startX;
            const dy = event.clientY - drag.startY;
            if (!drag.moved && Math.hypot(dx, dy) < PAN_DRAG_THRESHOLD_PX) return;
            drag.moved = true;
            if (!isPanning) setIsPanning(true);
            applyView({ zoom: view.zoom, panX: drag.panX + dx, panY: drag.panY + dy });
          }}
          onPointerUp={(event) => {
            if (panDragRef.current?.pointerId !== event.pointerId) return;
            panDragRef.current = null;
            setIsPanning(false);
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
              event.currentTarget.releasePointerCapture(event.pointerId);
            }
          }}
          onPointerCancel={(event) => {
            if (panDragRef.current?.pointerId !== event.pointerId) return;
            panDragRef.current = null;
            setIsPanning(false);
          }}
        >
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded bg-white/75 text-sm text-slate-600">
              Rendering plan page...
            </div>
          )}

          <div
            className="relative"
            style={{
              width: renderedSize.width > 0 ? `${renderedSize.width}px` : "100%",
              height: renderedSize.height > 0 ? `${renderedSize.height}px` : "320px",
              transform: `translate(${view.panX}px, ${view.panY}px) scale(${view.zoom})`,
              transformOrigin: "0 0",
            }}
          >
          {currentPageImage ? (
            <img
              key={currentPageImage.url}
              src={currentPageImage.url}
              alt={`Plan page ${effectivePage}`}
              data-testid="plan-review-page-image"
              className="block h-full w-full rounded bg-white object-contain shadow-sm"
              draggable={false}
              onLoad={() => {
                setLoading(false);
                setLoadingError(null);
              }}
              onError={() => {
                setLoading(false);
                setLoadingError("Failed to load the plan page.");
              }}
            />
          ) : (
            <canvas ref={canvasRef} className="block rounded bg-white shadow-sm" />
          )}

          {!loadingError && displayBoxes.length > 0 && (
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              {displayBoxes.map((box, index) => {
                const isFocused = isBoxFocused(box, focusedKey, focusedLabel);
                const bbox = normalizeHighlightBbox(box.bbox as number[]);
                const fullPage = isFullPageBbox(bbox);
                const useMarker = isFocused && !fullPage && isTinyBbox(bbox);
                const overlayStyle = useMarker
                  ? bboxCenterMarkerStyle(bbox)
                  : bboxToOverlayStyle(bbox, { minSizePercent: isFocused ? 0.5 : 0.15 });

                return (
                  <div
                    key={`${box.focusKey || box.label || "box"}-${box.page}-${index}`}
                    data-testid="plan-review-overlay-box"
                    data-focused={isFocused ? "true" : "false"}
                    data-full-page={fullPage ? "true" : "false"}
                    className={`absolute ${useMarker ? "rounded-full border-2" : fullPage ? "border border-dashed" : "rounded border"} ${
                      overlayBoxClass(isFocused, fullPage, box.stage)
                    }`}
                    data-stage={box.stage || undefined}
                    style={overlayStyle}
                  />
                );
              })}
            </div>
          )}

          {!loadingError && showPins && displayPins.length > 0 && (
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              {displayPins.map((pin) => {
                const [x1, y1] = clampNormalizedBbox(pin.bbox);
                const regionStyle = bboxToOverlayStyle(pin.bbox);
                const isFocusedPin = focusedPinId === pin.id;
                const badge = (
                  <button
                    type="button"
                    data-testid="plan-review-pin-marker"
                    data-focused={isFocusedPin ? "true" : "false"}
                    aria-label={`Comment pin ${pin.number}`}
                    className={`pointer-events-auto absolute flex h-6 w-6 -translate-x-1/2 -translate-y-full items-center justify-center rounded-full rounded-bl-sm text-[11px] font-semibold text-white shadow-sm ${
                      isFocusedPin ? "bg-violet-800 ring-2 ring-violet-300" : "bg-violet-600 hover:bg-violet-700"
                    }`}
                    style={{ left: `${x1 * 100}%`, top: `${y1 * 100}%` }}
                    onClick={() => onPinClick?.(pin.id)}
                  >
                    {pin.number}
                  </button>
                );

                if (isPointPin(pin.bbox)) {
                  return <span key={`pin-${pin.id}`}>{badge}</span>;
                }

                return (
                  <span key={`pin-${pin.id}`}>
                    <button
                      type="button"
                      aria-label={`Comment region ${pin.number}`}
                      className={`pointer-events-auto absolute rounded border-2 border-dashed ${
                        isFocusedPin ? "border-violet-800 bg-violet-500/20" : "border-violet-600 bg-violet-500/10"
                      }`}
                      style={regionStyle}
                      onClick={() => onPinClick?.(pin.id)}
                    />
                    {badge}
                  </span>
                );
              })}
            </div>
          )}

          {pinMode && !loading && !loadingError && (
            <div
              data-testid="plan-review-pin-capture"
              className="absolute inset-0 z-20 cursor-crosshair"
              onMouseDown={(event) => {
                const rect = event.currentTarget.getBoundingClientRect();
                setDragStart(pointerToNormalized(rect, event.clientX, event.clientY));
                setDragCurrent(null);
              }}
              onMouseMove={(event) => {
                if (!dragStart) return;
                const rect = event.currentTarget.getBoundingClientRect();
                setDragCurrent(pointerToNormalized(rect, event.clientX, event.clientY));
              }}
              onMouseUp={(event) => {
                if (!dragStart) return;
                const rect = event.currentTarget.getBoundingClientRect();
                const end = pointerToNormalized(rect, event.clientX, event.clientY);
                const bbox = buildAnchorBbox(dragStart, end);
                setDragStart(null);
                setDragCurrent(null);
                onPinPlaced?.(
                  effectivePage,
                  currentPageImage ? imageBboxToPageBbox(bbox, currentPageImage) : bbox
                );
              }}
              onMouseLeave={() => {
                setDragStart(null);
                setDragCurrent(null);
              }}
            >
              {dragStart && dragCurrent && (
                <div
                  className="absolute rounded border-2 border-dashed border-violet-600 bg-violet-500/10"
                  style={{
                    left: `${Math.min(dragStart.x, dragCurrent.x) * 100}%`,
                    top: `${Math.min(dragStart.y, dragCurrent.y) * 100}%`,
                    width: `${Math.abs(dragCurrent.x - dragStart.x) * 100}%`,
                    height: `${Math.abs(dragCurrent.y - dragStart.y) * 100}%`,
                  }}
                />
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
